// Code/src/Context/ThemeContext.tsx

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

export type ThemeName = "light" | "dark";
// The user's stored choice. "system" (the default) means "follow the OS
// setting" rather than a fixed light/dark value — nothing is written to
// localStorage while on "system", matching the fallback both here and in
// the inline script in public/index.html already treat "nothing stored" as.
export type ThemePreference = "system" | ThemeName;

interface ThemeContextValue {
  theme: ThemeName;
  themePreference: ThemePreference;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const THEME_STORAGE_KEY = "pbs-profile-theme";

const applyThemeToDocument = (theme: ThemeName) => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (theme === "dark") {
    root.classList.add("dark");
  } else {
    root.classList.remove("dark");
  }
};

const prefersDarkScheme = (): boolean =>
  Boolean(
    window.matchMedia?.("(prefers-color-scheme: dark)").matches,
  );

const resolveTheme = (preference: ThemePreference): ThemeName =>
  preference === "system"
    ? prefersDarkScheme()
      ? "dark"
      : "light"
    : preference;

// Mirrors the inline script in public/index.html, which already applies the
// "dark" class to <html> before React loads (avoiding a flash of the wrong
// theme). Reading the same stored/system preference here keeps this state
// consistent with what's already on the page for that first render.
const getInitialPreference = (): ThemePreference => {
  try {
    const stored = window.localStorage.getItem(
      THEME_STORAGE_KEY,
    ) as ThemeName | null;
    if (stored === "light" || stored === "dark") {
      return stored;
    }
  } catch {
    // ignore storage errors
  }
  return "system";
};

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [preference, setPreference] = useState<ThemePreference>(
    getInitialPreference,
  );
  const [theme, setTheme] = useState<ThemeName>(() =>
    resolveTheme(getInitialPreference()),
  );

  useEffect(() => {
    applyThemeToDocument(theme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // While following "system", react live to OS theme changes instead of
  // only resolving once at mount.
  useEffect(() => {
    if (preference !== "system" || !window.matchMedia) return;
    const query = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (event: MediaQueryListEvent) => {
      const next: ThemeName = event.matches ? "dark" : "light";
      setTheme(next);
      applyThemeToDocument(next);
    };
    query.addEventListener("change", handleChange);
    return () => query.removeEventListener("change", handleChange);
  }, [preference]);

  const toggleTheme = useCallback(() => {
    setPreference((prev) => {
      const next: ThemePreference =
        prev === "system" ? "light" : prev === "light" ? "dark" : "system";
      const resolved = resolveTheme(next);
      setTheme(resolved);
      applyThemeToDocument(resolved);
      try {
        if (next === "system") {
          window.localStorage.removeItem(THEME_STORAGE_KEY);
        } else {
          window.localStorage.setItem(THEME_STORAGE_KEY, next);
        }
      } catch {
        // ignore storage errors
      }
      return next;
    });
  }, []);

  const value: ThemeContextValue = {
    theme,
    themePreference: preference,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextValue => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return ctx;
};
